import React, { useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  IconButton,
  Divider,
  Slider,
  Card,
  CardContent,
  Button,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import MailOutlineIcon from '@mui/icons-material/MailOutline';

interface PayAsYouNeedModalProps {
  open: boolean;
  onClose: () => void;
}

const marks = [1, 10, 50, 100, 250, 500];

const PayAsYouNeedModal: React.FC<PayAsYouNeedModalProps> = ({ open, onClose }) => {
  const [amount, setAmount] = useState<number>(50);
  const pricePerTb = 8; // $/TB displayed in UI
  const total = useMemo(() => amount * pricePerTb, [amount, pricePerTb]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          borderRadius: '16px',
          width: '528px',
          height: '553px',
          maxWidth: '100%',
          overflow: 'hidden',
        },
      }}
    >
      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            height: '64px',
            px: 3,
            borderBottom: '1px solid #E9EAEB',
            bgcolor: 'white',
          }}
        >
          <Typography sx={{ fontWeight: 600, fontSize: '18px', color: '#101828' }}>
            Pay as you need
          </Typography>
          <IconButton onClick={onClose} aria-label="close">
            <CloseIcon sx={{ color: '#A4A7AE' }} />
          </IconButton>
        </Box>

        {/* Body */}
        <Box sx={{ p: 3, flex: 1, display: 'flex', flexDirection: 'column' }}>
          {/* Select amount */}
          <Box>
            <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#414651' }}>
              Select amount*
            </Typography>
            <Typography sx={{ fontSize: '14px', color: '#535862', mt: 0.5 }}>
              Only pay for what you use each month.
            </Typography>

            <Box sx={{ mt: 2 }}>
              <Slider
                value={amount}
                onChange={(_, v) => setAmount(v as number)}
                min={1}
                max={500}
                step={1}
                sx={{
                  color: '#617BFF',
                  height: 6,
                  '& .MuiSlider-thumb': {
                    width: 18,
                    height: 18,
                    bgcolor: '#fff',
                    border: '2px solid #617BFF',
                  },
                  '& .MuiSlider-rail': { bgcolor: '#E5E7EB' },
                }}
              />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                {['1TB', '10TB', '50TB', '100TB', '250TB', '500TB', 'Custom'].map((label) => (
                  <Typography key={label} sx={{ fontSize: '12px', color: '#6B7280' }}>
                    {label}
                  </Typography>
                ))}
              </Box>
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Price summary */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
              <Typography sx={{ fontSize: '40px', fontWeight: 700, color: '#101828' }}>
                ${total}
              </Typography>
              <Typography sx={{ color: '#6B7280' }}>${pricePerTb}/TB</Typography>
            </Box>
            <Typography sx={{ color: '#6B7280', mt: 0.5 }}>Billed the day you purchase.</Typography>
          </Box>

          {/* Credit card */}
          <Card
            sx={{
              mt: 2.5,
              borderRadius: '12px',
              boxShadow: 'none',
              bgcolor: 'white',
              border: '1px solid #E9EAEB',
            }}
          >
            <CardContent sx={{ p: 2.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 26,
                      borderRadius: '4px',
                      bgcolor: '#1A1F71',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 800,
                      fontSize: '12px',
                      letterSpacing: 1,
                    }}
                  >
                    VISA
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#101828' }}>
                      Visa ending in 1234
                    </Typography>
                    <Typography sx={{ fontSize: '12px', color: '#6B7280' }}>
                      Expiry 06/2025
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.5 }}>
                      <MailOutlineIcon sx={{ fontSize: 16, color: '#6B7280' }} />
                      <Typography sx={{ fontSize: '12px', color: '#6B7280' }}>
                        billing@flashback.tech
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                <Button
                  variant="outlined"
                  sx={{
                    textTransform: 'none',
                    borderRadius: '8px',
                    borderColor: '#E9EAEB',
                    color: '#101828',
                    '&:hover': { borderColor: '#D0D5DD', bgcolor: 'rgba(16,24,40,0.02)' },
                  }}
                >
                  Change
                </Button>
              </Box>
            </CardContent>
          </Card>

          {/* Footer actions */}
          <Box sx={{ mt: 'auto', pt: 2.5 }}>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
              <Button onClick={onClose} sx={{ textTransform: 'none', color: '#414651' }}>
                Cancel
              </Button>
              <Button
                variant="contained"
                sx={{
                  bgcolor: '#617BFF',
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: '8px',
                  '&:hover': { bgcolor: '#4A67E3' },
                }}
              >
                Purchase
              </Button>
            </Box>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default PayAsYouNeedModal;
